import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Badge,
  Button,
  Modal,
} from "react-bootstrap";
import { toast } from "react-toastify";
import customerService from "../../services/customerService";
import {
  formatDateToDDMMYYYY,
  formatDateTimeToDDMMYYYY12Hr,
} from "../../utils/dateUtils";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import InfoPill from "../Common/InfoPill";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";

const CustomerDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await customerService.get(id);
        setCustomer(data.customer || null);
        setOrders(data.orders || []);
      } catch (e) {
        toast.error("Failed to load customer details");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleToggleStatus = () => {
    if (!customer) return;
    setNewStatus(!customer.is_active);
    setShowStatusModal(true);
  };

  const confirmToggleStatus = async () => {
    if (!customer) return;
    
    setUpdatingStatus(true);

    try {
      await customerService.toggleStatus(customer.id, newStatus);
      toast.success(`Customer ${newStatus ? "activated" : "deactivated"} successfully`);
      setCustomer((prev) => ({ ...prev, is_active: newStatus }));
      setShowStatusModal(false);
      setNewStatus(null);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update customer status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleBack = () => {
    navigate("/admin/customers");
  };

  const getStatusColor = (status) => {
    const colors = {
      placed: "primary",
      accepted: "info",
      processing: "warning",
      packed: "warning",
      out_for_delivery: "info",
      delivered: "success",
      cancelled: "danger",
      returned: "secondary",
    };
    return colors[status] || "secondary";
  };

  const toTitleCase = (str) => {
    if (!str) return "";
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Transform orders data to include searchable fields
  const transformedOrders = useMemo(() => {
    if (!orders || !Array.isArray(orders)) {
      return [];
    }

    return orders.map((order) => {
      // Create searchable items field with product names and quantities
      const formatItemQuantity = (item) => {
        if (
          item.order_quantity != null &&
          item.product_quantity != null
        ) {
          const unitDisplay = item.unit ? item.unit : "";
          return `${item.order_quantity} × ${item.product_quantity}${unitDisplay}`;
        }
        if (item.quantity != null) {
          const unitDisplay = item.unit ? ` ${item.unit}` : "";
          return `x${item.quantity}${unitDisplay}`;
        }
        return "";
      };

      const itemsSearchable = (order.items && Array.isArray(order.items))
        ? order.items
            .map(
              (item) =>
                `${item.product_name || ""} ${formatItemQuantity(item)}`
            )
            .join(" ")
        : "";

      return {
        ...order,
        items_searchable: itemsSearchable,
      };
    });
  }, [orders]);

  // Orders table headers
  const ordersHeaders = [
    {
      prop: "id",
      title: "Order ID",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Link
          to={`/admin/orders/details/${row.id}`}
          className="text-decoration-none fw-bold text-primary"
        >
          {row.id}
        </Link>
      ),
    },
    {
      prop: "order_status",
      title: "Status",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Badge bg={getStatusColor(row.order_status)}>
          {toTitleCase(row.order_status || "placed")}
        </Badge>
      ),
    },
    {
      prop: "payment_status",
      title: "Payment",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Badge
          bg={
            row.payment_status === "paid" || row.payment_status === "completed"
              ? "success"
              : row.payment_status === "failed"
              ? "danger"
              : "warning"
          }
        >
          {row.payment_status?.charAt(0).toUpperCase() +
            row.payment_status?.slice(1).toLowerCase()}
        </Badge>
      ),
    },
    {
      prop: "final_amount",
      title: "Amount",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <div>
          <div className="fw-bold">₹{parseFloat(row.final_amount || 0).toFixed(2)}</div>
          {row.delivery_charge > 0 && (
            <small className="text-muted">
              +₹{row.delivery_charge} delivery
            </small>
          )}
        </div>
      ),
    },
    {
      prop: "created_at",
      title: "Order Date",
      isFilterable: true,
      isSortable: true,
      cell: (row) => formatDateTimeToDDMMYYYY12Hr(row.created_at),
    },
    {
      prop: "items_searchable",
      isFilterable: true,
      title: "Items",
      cell: (row) => {
        const formatItemQuantity = (item) => {
          if (
            item.order_quantity != null &&
            item.product_quantity != null
          ) {
            const unitDisplay = item.unit ? item.unit : "";
            return `${item.order_quantity} × ${item.product_quantity}${unitDisplay}`;
          }
          if (item.quantity != null) {
            const unitDisplay = item.unit ? ` ${item.unit}` : "";
            return `x${item.quantity}${unitDisplay}`;
          }
          return "";
        };

        return (
          <div className="d-flex flex-wrap gap-1">
            {row.items && Array.isArray(row.items) && row.items.length > 0 ? (
              row.items.slice(0, 2).map((item, index) => (
                <Badge key={index} bg="secondary" className="me-1">
                  {item.product_name || "Unknown"} ({formatItemQuantity(item)})
                </Badge>
              ))
            ) : (
              <span className="text-muted">No items</span>
            )}
            {row.items && Array.isArray(row.items) && row.items.length > 2 && (
              <Badge bg="light" text="dark">
                +{row.items.length - 2} more
              </Badge>
            )}
          </div>
        );
      },
    },
  ];

  // Helper function to render profile image
  const renderProfileImage = (imageUrl, altText) => {
    if (!imageUrl) {
      return (
        <div
          style={{
            width: "170px",
            height: "170px",
            backgroundColor: "#ddd",
            borderRadius: "10%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
            boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
          }}
        >
          <span style={{ fontSize: "3rem" }}>👤</span>
        </div>
      );
    }

    return (
      <img
        src={imageUrl}
        alt={altText}
        style={{
          width: "170px",
          height: "170px",
          objectFit: "cover",
          borderRadius: "10%",
          boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
          cursor: "pointer",
          display: "block",
          margin: "0 auto",
        }}
        onClick={() => window.open(imageUrl, "_blank")}
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = "/not-img-found.png";
        }}
      />
    );
  };

  if (loading) {
    return (
      <Container className="view-form">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!customer) {
    return (
      <Container className="view-form">
        <div className="text-center mt-4">
          <h4>Customer not found</h4>
          <Button variant="primary" onClick={handleBack}>
            Back to Customers
          </Button>
        </div>
      </Container>
    );
  }

  const fullName =
    `${customer?.first_name || ""} ${customer?.last_name || ""}`.trim() || "-";
  const createdDate = customer?.created_at
    ? formatDateToDDMMYYYY(customer.created_at)
    : "-";

  return (
    <Container className="view-form">
      <Row>
        <Col lg={12}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">Customer Details</h5>
            <div className="d-flex gap-2">
              {/* <Button variant="outline-primary" size="sm" onClick={handleBack}>
                <i className="bi bi-arrow-left-short fs-3"></i>
              </Button> */}
              <Button
                size="sm"
                onClick={handleToggleStatus}
                disabled={updatingStatus}
                title={customer.is_active ? "Deactivate customer" : "Activate customer"}
                style={{
                  backgroundColor: customer.is_active ? "#dc3545" : "#28a745",
                  borderColor: customer.is_active ? "#dc3545" : "#28a745",
                  color: "#fff",
                  opacity: updatingStatus ? 0.6 : 1,
                }}
              >
                {updatingStatus ? (
                  <Spinner size="sm" animation="border" />
                ) : customer.is_active ? (
                  <>
                    <FaTimesCircle className="me-1" /> Deactivate
                  </>
                ) : (
                  <>
                    <FaCheckCircle className="me-1" /> Activate
                  </>
                )}
              </Button>
            </div>
          </div>
          <Card className="shadow">
            <CardBody>
              <Row>
                <Col lg={2} className="bg-light rounded-4">
                  <Row>
                    <Col lg={12} className="my-2">
                      <div className="image-box">
                        {renderProfileImage(
                          customer.image_url,
                          `Profile Image - ${fullName}`
                        )}
                      </div>

                      <Col lg={12} className="my-2 mt-4 text-center">
                        <label className="fw-bold">
                          <h6>Full Name</h6>
                        </label>
                        <h5
                          className="mb-0 text-center"
                          style={{
                            textAlign: "left",
                            borderBottom: "1px solid #ccc",
                            paddingBottom: "20px",
                          }}
                        >
                          {fullName}
                        </h5>
                      </Col>
                      <Col lg={12} className="my-2 text-center">
                        <label className="fw-bold">
                          <h6>Status</h6>
                        </label>
                        <h5
                          className="mb-0 text-center"
                          style={{
                            textAlign: "left",
                            borderBottom: "1px solid #ccc",
                            paddingBottom: "20px",
                          }}
                        >
                          <Badge
                            bg={customer.is_active ? "success" : "secondary"}
                          >
                            {customer.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </h5>
                      </Col>
                    </Col>
                  </Row>
                </Col>
                <Col lg={10}>
                  {/* Personal Information Section */}
                  <Row className="mx-3 my-2 view-form">
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Email</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {customer.email || "-"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Phone</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {customer.phone || "-"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Created Date</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {createdDate}
                      </p>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Orders Section */}
      <Row className="mt-4">
        <Col lg={12}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">
              Orders ({orders?.length || 0})
            </h5>
          </div>
          <Card className="shadow">
            <CardBody>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Loading orders...</p>
                </div>
              ) : orders && orders.length > 0 ? (
                <DatatableWrapper
                  body={transformedOrders}
                  headers={ordersHeaders}
                  paginationOptionsProps={{
                    initialState: {
                      rowsPerPage: 5,
                      options: [5, 10, 15, 20],
                    },
                  }}
                >
                  {/* Controls */}
                  <div className="d-flex align-items-center justify-content-start flex-wrap gap-2 mb-3">
                    <div className="flex-grow-1" style={{ maxWidth: "300px" }}>
                      <Filter
                        placeholder="Search orders..."
                        className="w-100"
                      />
                    </div>
                    <PaginationOptions />
                    <InfoPill left="Total" right={transformedOrders?.length} />
                  </div>

                  {/* Table */}
                  <div className="table-responsive shadow-sm">
                    <table className="table table-striped table-bordered align-middle">
                      <TableHeader />
                      <TableBody />
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-start mt-3">
                    <Pagination classes={{
                      button: "bg-dark",
                    }} />
                  </div>
                </DatatableWrapper>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-shopping-cart fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No Orders Found</h5>
                  <p className="text-muted">
                    This customer hasn't placed any orders yet.
                  </p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Status Change Confirmation Modal */}
      <Modal
        show={showStatusModal}
        onHide={() => {
          setShowStatusModal(false);
          setNewStatus(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {newStatus ? "Activate Customer" : "Deactivate Customer"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to {newStatus ? "activate" : "deactivate"} customer{" "}
          <strong>
            {customer?.first_name || ""} {customer?.last_name || ""}
          </strong>
          ?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowStatusModal(false);
              setNewStatus(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant={newStatus ? "success" : "danger"}
            onClick={confirmToggleStatus}
            disabled={updatingStatus}
          >
            {updatingStatus
              ? "Updating..."
              : newStatus
              ? "Activate"
              : "Deactivate"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CustomerDetails;
